// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, { useEffect, useRef } from 'react';
import { generateEventSections } from '../EventCalendarHelpers';
import debounce from 'lodash.debounce';
import type { EventCalendarData } from 'cms/resolvers/eventCalendarResolver';
import Button from '2-core-elements/Buttons/Button';

interface EventsWrapperProps {
    data: EventCalendarData;
    hasNoResults?: boolean;
    onResetFilters?: () => void;
    hideFilters?: boolean;
    hasActiveFilters?: boolean;
}

const EventsWrapper: React.FC<EventsWrapperProps> = ({ data, hasNoResults, onResetFilters, hideFilters, hasActiveFilters }) => {
    const lastHighlightedMonthRef = useRef<string | null>(null);

    useEffect(() => {
        const monthNavItems = document.querySelectorAll('.nav-month-selector');
        const monthSections = document.querySelectorAll('.event-calendar-month');

        const highlightCurrentMonth = (): void => {
            let currentMonth = null;
            const eventNavBar = document.querySelector('.event-nav-bar') as HTMLElement;
            const navBarHeight = eventNavBar ? eventNavBar.offsetHeight : 0;

            monthSections.forEach(section => {
                const rect = section.getBoundingClientRect();
                if (rect.top <= navBarHeight && rect.bottom > navBarHeight) {
                    currentMonth = section.id;
                }
            });

            monthNavItems.forEach(item => {
                item.classList.remove('highlight');
            });
            if (currentMonth) {
                const currentNavItem = document.querySelector(`.nav-month-selector[data-month="${currentMonth}"]`);
                if (currentNavItem) {
                    currentNavItem.classList.add('highlight');
                    if (lastHighlightedMonthRef.current !== currentMonth) {
                        currentNavItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        lastHighlightedMonthRef.current = currentMonth;
                    }
                }
            }
        };

        highlightCurrentMonth();

        const debouncedHighlight = debounce(highlightCurrentMonth, 150);
        window.addEventListener('scroll', debouncedHighlight);

        return () => {
            window.removeEventListener('scroll', debouncedHighlight);
        };
    }, []);

    useEffect(() => {
        if (hasNoResults) {
            const el = document.querySelector('.events-wrapper');
            if (el) {
                const elTop = el.getBoundingClientRect().top + window.scrollY;
                const scrollTarget = elTop - 64;
                if (window.scrollY > scrollTarget - 10) { // 10px buffer for tolerance
                    console.info('Scrolling to .events-wrapper minus 48px due to no results');
                    window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
                }
            }
        }
    }, [hasNoResults]);

    return (
        <div className={`events-wrapper${hideFilters ? ' no-filters' : ''}`}>
            {hasNoResults ? (
                <div className="no-results">
                    <p className="no-results-message">
                        {hasActiveFilters
                            ? 'No events found using those filters. Try adjusting your search or clearing all filters.'
                            : 'There are currently no events scheduled.'}
                    </p>
                    {!hideFilters && (
                        <Button skin="2" size="sm" onClick={onResetFilters}>
                            Clear All Filters
                        </Button>
                    )}
                </div>
            ) : (
                generateEventSections(data)
            )}
        </div>
    );
};

export default EventsWrapper;
